"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Phone, Mail, MapPin, Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactDetailsForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  return <section className="bg-background py-24 px-4">
      <div className="mx-auto max-w-screen-xl">
        <FormProvider className="w-full" formId="contact-form-details" sectionName="contact-details-form" defaultValues={{
        name: "",
        email: "",
        phone: "",
        message: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2,
          maxLength: 50
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          pattern: "phone"
        },
        message: {
          required: true,
          minLength: 10
        }
      }} submitText="Start Dispatching" successMessage="Thank you! We'll get in touch with you shortly.">
          <div ref={ref} className={`transition-all duration-[var(--duration-fast)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <Card className="shadow-strong hover-lift px-0 py-0">
              <div className="grid md:grid-cols-2 gap-0">
                {}
                <div className="bg-accent text-foreground-accent p-12 rounded-l-xl flex flex-col justify-center gap-8">
                  <div>
                    <h2 className="text-3xl font-bold mb-2">Get in Touch</h2>
                    <p className="text-foreground-accent/80">
                      Ready to boost your trucking business? Contact us today.
                    </p>
                  </div>

                  <div className="space-y-6">
                    <div className="flex items-start gap-4 group">
                      <div className="p-3 bg-primary rounded-md transition-all duration-[var(--duration-medium)] group-hover:scale-110">
                        <Phone className="size-5 text-foreground-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-foreground-accent/70 mb-1">Phone</p>
                        <p className="font-semibold">(888) 218-8355</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 group">
                      <div className="p-3 bg-primary rounded-md transition-all duration-[var(--duration-medium)] group-hover:scale-110">
                        <Mail className="size-5 text-foreground-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-foreground-accent/70 mb-1">Email</p>
                        <p className="font-semibold">dispatch@oryxfleet.com</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 group">
                      <div className="p-3 bg-primary rounded-md transition-all duration-[var(--duration-medium)] group-hover:scale-110">
                        <MapPin className="size-5 text-foreground-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-foreground-accent/70 mb-1">Address</p>
                        <p className="font-semibold">30 N Gould St Ste R, Sheridan, WY 82801</p>
                      </div>
                    </div>
                  </div>
                </div>

                {}
                <div className="p-12">
                  <ContactFormContent />
                </div>
              </div>
            </Card>
          </div>
        </FormProvider>
      </div>
    </section>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="h-full flex flex-col items-center justify-center text-center p-6 bg-primary/10 rounded-lg">
        <div className="size-16 bg-primary rounded-full flex items-center justify-center mb-4">
          <Mail className="size-8 text-foreground-primary" />
        </div>
        <h3 className="text-2xl font-bold mb-2">Message Sent!</h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <div>
          <h3 className="text-2xl font-bold mb-2">Start Dispatching Today</h3>
          <p className="text-muted-foreground">
            Fill out the form and we'll contact you within 24 hours.
          </p>
        </div>

        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className="rounded-lg" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="rounded-lg" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[\d\s\-\+\(\)]+$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" className="rounded-lg" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Message</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us about your dispatching needs..." className="rounded-lg min-h-32" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-destructive text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full bg-primary hover:bg-primary-hover active:bg-primary-active text-foreground-primary shadow-medium hover:shadow-strong transition-all duration-[var(--duration-medium)] hover:-translate-y-0.5">
          {isSubmitting ? <>
              <Loader className="animate-spin size-4" />
              Sending...
            </> : "Start Dispatching"}
        </Button>
      </div>
    </Form>;
}